import { makeAutoObservable, action } from "mobx";
import { AsyncStateStore } from "@/utils/AsyncStateStore";
import { timeSeries, DataSeries } from "@/endpoints/dryp/timeSeries";
import { downsampleLTTB, DEFAULT_DOWNSAMPLE_THRESHOLD } from "@/utils/downsample";

const DEFAULT_COLOR = "#abc7ff";

export class GraphStore {
	parameter: Dryp.Parameter;
	customColor?: string;
	visible: boolean = true;
	order: number = 0;

	private rawData: DataSeries = [];
	private cachedDisplayData: Array<[number, number | null]> = [];
	private zoomMin: number | null = null;
	private zoomMax: number | null = null;

	readonly timeSeriesApi = new AsyncStateStore(timeSeries.getOne);
	private structureId: Dryp.StructureId;

	constructor(structureId: Dryp.StructureId, parameter: Dryp.Parameter, order: number = 0) {
		this.structureId = structureId;
		this.parameter = parameter;
		this.order = order;

		makeAutoObservable(this, {
			setColor: action,
			setVisible: action,
			toggleVisible: action,
			setOrder: action,
			setZoomRange: action,
			fetch: action,
			clear: action,
			updateDisplayData: action,
		});
	}

	/** Recompute display data from raw data and zoom range */
	public updateDisplayData() {
		if (this.rawData.length === 0) {
			this.cachedDisplayData = [];
			return;
		}

		// Filter data by zoom range if set
		let filteredData = this.rawData;
		if (this.zoomMin !== null || this.zoomMax !== null) {
			filteredData = this.rawData.filter(([time]) => {
				const afterMin = this.zoomMin === null || time >= this.zoomMin;
				const beforeMax = this.zoomMax === null || time <= this.zoomMax;
				return afterMin && beforeMax;
			});
		}

		// If data is small enough, return as-is (no downsampling needed)
		if (filteredData.length <= DEFAULT_DOWNSAMPLE_THRESHOLD) {
			this.cachedDisplayData = filteredData.map(([time, value]) => [time, value ?? null]);
			return;
		}

		// Apply LTTB downsampling to the visible range
		const downsampled = downsampleLTTB(
			filteredData.map(([time, value]) => [time, value ?? null]),
			DEFAULT_DOWNSAMPLE_THRESHOLD,
		);
		this.cachedDisplayData = downsampled as Array<[number, number | null]>;
	}

	get id() {
		return this.parameter.id;
	}

	get name() {
		return this.parameter.displayName || this.parameter.name;
	}

	get unit() {
		const symbol = this.parameter.unit?.symbol;
		const unitName = this.parameter.unit?.name;
		const symbolText = symbol ? `(${symbol})` : "";
		return unitName ? `${unitName} ${symbolText}`.trim() : this.name;
	}

	get unitSymbol() {
		return this.parameter.unit?.symbol || "";
	}

	get color() {
		return this.customColor ?? this.parameter.visualProperties?.color ?? DEFAULT_COLOR;
	}

	setColor(color: string) {
		this.customColor = color;
	}

	get loading() {
		return this.timeSeriesApi.loading;
	}

	get loaded() {
		return this.timeSeriesApi.loaded;
	}

	get error() {
		return this.timeSeriesApi.error;
	}

	get isNotConfigured(): boolean {
		const error = this.timeSeriesApi.error as { status?: number } | null;
		return error?.status === 404;
	}

	get decimals(): number {
		return this.parameter.visualProperties?.decimals ?? 2;
	}

	get data(): Array<[number, number | null]> {
		return this.cachedDisplayData;
	}

	formatValue(value: number | null): string {
		if (value == null) return "";
		const factor = Math.pow(10, this.decimals);
		return String(Math.round(value * factor) / factor);
	}

	get hasData() {
		return this.rawData.length > 0;
	}

	get rawDataLength() {
		return this.rawData.length;
	}

	async fetch(from: string, to: string) {
		const paramId = this.parameter.id;
		const cacheKey = `timeseries:${this.structureId}:${paramId}:${from}:${to}`;

		const cached = AsyncStateStore.getCache<DataSeries>(cacheKey);
		if (cached) {
			this.rawData = cached;
			this.timeSeriesApi.updateData(cached);
			this.updateDisplayData();
			return;
		}

		await this.timeSeriesApi.fetch({
			structureId: this.structureId,
			paramId,
			from,
			to,
		});

		if (this.timeSeriesApi.data) {
			this.rawData = this.timeSeriesApi.data;
			AsyncStateStore.setCache(cacheKey, this.rawData);
			this.updateDisplayData();
		}
	}

	/** Update zoom range - triggers re-downsampling of visible data */
	setZoomRange(min: number | null, max: number | null) {
		this.zoomMin = min;
		this.zoomMax = max;
		this.updateDisplayData();
	}

	setVisible(visible: boolean) {
		this.visible = visible;
	}

	toggleVisible() {
		this.visible = !this.visible;
	}

	setOrder(order: number) {
		this.order = order;
	}

	clear() {
		this.timeSeriesApi.clear();
	}

	clearCache(from: string, to: string) {
		const cacheKey = `timeseries:${this.structureId}:${this.parameter.id}:${from}:${to}`;
		AsyncStateStore.clearCache(cacheKey);
	}
}
